import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { FC, useContext, useMemo } from 'react';
import { ReactComponent as LinkIcon } from "../../../assets/icons/link.svg";
import { DashboardContext } from "../../../contexts/DashboardContext";
import { scrollbarStyle } from "../../../shared/dashboard";
import theme from "../../../theme";

const useStyles = makeStyles(() => ({
    dialog: {
        "& .MuiDialog-paper": {
            width: 'auto',
            maxWidth: 500,
            minWidth: 500,
            height: 'fit-content',
            minHeight: '30vh',
            padding: '16px 24px 8px 24px',
            boxShadow: '0px 20px 30px -5px rgba(16, 24, 40, 0.06)',
            borderRadius: 24,
        }
    },
    dialogTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: 'unset',
        color: 'black',
        fontWeight: 'bold',
        fontFamily: 'Inter',
        fontSize: '1.4rem',
        gap: 8,
    },
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: '8px 0',
        height: '100%',
        overflow: 'hidden',
    },
    dialogActions: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
    },
    headerIcon: {
        stroke: theme.colors.neutral['500'],
        width: 36,
        height: 36,
    },
    contentIcon: {
        stroke: theme.colors.neutral['500'],
        width: 28,
        height: 28,
    },
    content: {
        paddingLeft: 8,
        paddingRight: 8,
        width: '100%',
        maxHeight: 290,
        overflowY: 'auto',
        ...scrollbarStyle,
    },
    cancelBtn: {
        width: 100,
        height: 40,
        borderRadius: 40,
        background: theme.colors.neutral['100'],
        color: theme.colors.neutral['600'],
        textTransform: 'none',
        fontWeight: 'bold',
        transition: 'ease-in-out 300ms',
    },
    url: {
        display: 'inline-block',
        maxWidth: '36ch',
        fontFamily: 'Inter',
        fontWeight: 700,
        color: theme.palette.primary.main,
        textDecoration: 'none',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        lineClamp: 1,
        "&:hover": {
            textDecoration: 'none !important',
        },
    },
    linkOnlyUrl: {
    },
}))

const DashboardSavedLinksModal: FC<{  open: boolean; onClose: () => void }> = ({ open, onClose }) => {
    const classes = useStyles();
    const { dashboard } = useContext(DashboardContext);

    const savedLinks = useMemo(() => dashboard?.externalLinks ?? [], [dashboard]);

    return (<>
        <Dialog className={classes.dialog} open={open} onClose={() => onClose()}>
            <DialogTitle className={classes.dialogTitle}>
                <LinkIcon className={classes.headerIcon} />
                <span>{`Saved links for ${dashboard?.title}`}</span>
            </DialogTitle>
            <Typography color={theme.colors.neutral['600']} pt={1}>
                {`Links saved from emails for ${dashboard?.title}, including deck links.`}
            </Typography>
            <Divider sx={{ margin: '8px 0', width: '100%' }} />
            <DialogContent className={classes.dialogContent}>
                <Stack className={classes.content} spacing={1} alignItems="flex-start">
                    {savedLinks.map((link, i) => (
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start"
                            key={'dashboard-saved-links-modal-90-' + i}>
                                <LinkIcon className={classes.contentIcon} />
                                <Typography component="a"
                                    className={classes.url}
                                    href={link}
                                    target="_blank"
                                    rel="noopener noreferrer">
                                    {link.replace('https://', '').replace('http://', '')}
                                </Typography>
                        </Stack>
                    ))}
                </Stack>
            </DialogContent>
            <Divider sx={{ margin: '8px 0'}} />
            <DialogActions className={classes.dialogActions}>
                <Button className={classes.cancelBtn} onClick={onClose}> {'Close'} </Button>
            </DialogActions>
        </Dialog>
    </>);
}

export default DashboardSavedLinksModal;